import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useChatContext } from '../../context/ChatContext';
import { useFileTreeContext, UploadTracker, UPLOAD_DONE_DWELL_MS } from '../../context/FileTreeContext';
import { useAuthContext } from '../../context/AuthContext';
import { useArtifactsPanel } from '../../context/ArtifactsPanelContext';
import { useQuota } from '../../context/QuotaContext';
import { detectArtifactIntent } from '../../utils/detectArtifactIntent';
import { deriveReportTitle } from '../../utils/reportContent';
import apiService from '../../services/api';
import {
  Send,
  Paperclip,
  ArrowDown,
  X,
  FileText,
  StopCircle,
  BarChart3,
  FileSearch,
  Sparkles,
  ShieldCheck,
  ArrowLeftRight,
  Check,
  BookOpen,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Plus,
  Wrench,
  ChevronLeft,
  Scale,
  Landmark,
} from 'lucide-react';
import TaseImportModal from './TaseImportModal';
import {
  ChatMessage,
  Tool,
  FileNode,
  DocumentStatus,
  RecipeJobAccepted,
  RecipeJobStatus,
  LegalSkill,
  ProductUsageCategory,
} from '../../types';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-hot-toast';
import ArtifactRenderer from '../Artifacts/ArtifactRenderer';
import MarkdownRenderer from '../Artifacts/MarkdownRenderer';
import ClarifyQuestions from './ClarifyQuestions';
import { parseClarifyBlock } from '../../utils/parseClarifyBlock';
import { isCompactionMessage } from '../../utils/compaction';
import { AgentAvatar } from '../AgentAvatar';
import { personaForDomain, AgentDomain } from '../../utils/agentCatalog';

// Curated user-facing chat modes.
//
// This is deliberately NOT the raw MCP tool catalog (~57 tools) — that surface
// is meant for the agent (propose_prompt_change, get_system_logs, scrub_text,
// …) and is meaningless to an end user. Each mode here is wired server-side in
// `call_w3_chat` (backend/app/api/chat.py): selecting it shapes the next answer
// via a system-prompt addition (and model pin for deep analysis). Ids must match
// the backend `_TOOL_MODE_PROMPTS` keys.
const USER_TOOLS: Tool[] = [
  { id: 'deep_analysis', name: 'ניתוח מעמיק', description: 'ניתוח פיננסי מפורט עם יחסים, מגמות והקשר', icon: 'Sparkles' },
  { id: 'verify_data', name: 'אימות נתונים', description: 'הצלבת מספרים בין מסמכים ואיתור אי-התאמות', icon: 'ShieldCheck' },
  { id: 'compare_periods', name: 'השוואת תקופות', description: 'השוואה בין רבעונים או שנים', icon: 'ArrowLeftRight' },
];

const toolIcons: Record<string, React.ReactNode> = {
  Sparkles: <Sparkles className="h-4 w-4" />,
  ShieldCheck: <ShieldCheck className="h-4 w-4" />,
  ArrowLeftRight: <ArrowLeftRight className="h-4 w-4" />,
};

const addTempFileNode = (nodes: FileNode[], tempNode: FileNode): FileNode[] => {
  return nodes.map((node) => {
    if (node.id === 'org-root' && node.type === 'folder') {
      const existingChildren = node.children || [];
      return {
        ...node,
        children: [tempNode, ...existingChildren],
        fileCount: (node.fileCount || 0) + 1,
      };
    }
    if (node.children && node.children.length > 0) {
      return { ...node, children: addTempFileNode(node.children, tempNode) };
    }
    return node;
  });
};

const removeNodeById = (nodes: FileNode[], nodeId: string): FileNode[] => {
  return nodes
    .filter((node) => node.id !== nodeId)
    .map((node) => {
      if (!node.children || node.children.length === 0) return node;
      const nextChildren = removeNodeById(node.children, nodeId);
      const removedCount = (node.children.length - nextChildren.length);
      return {
        ...node,
        children: nextChildren,
        fileCount: typeof node.fileCount === 'number' ? Math.max(0, node.fileCount - removedCount) : node.fileCount,
      };
    });
};

const buildNodeLabelMap = (nodes: FileNode[], labels = new Map<string, string>()): Map<string, string> => {
  for (const node of nodes) {
    labels.set(node.id, node.name);
    if (node.children && node.children.length > 0) {
      buildNodeLabelMap(node.children, labels);
    }
  }
  return labels;
};

/** Augment a label map with filenames from in-flight / recently-completed
    uploads so a freshly-uploaded doc shows its filename in the scope chip
    even before `refreshTree` has fetched the new tree. Without this the
    chip flashes the raw id (`doc-20`) for a beat. */
const mergeUploadLabels = (
  labels: Map<string, string>,
  uploads: Array<{ documentId: number; filename: string }>,
): Map<string, string> => {
  for (const u of uploads) {
    const key = `doc-${u.documentId}`;
    if (!labels.has(key)) labels.set(key, u.filename);
  }
  return labels;
};

// Staged "thinking" indicator (Claude-style). Instead of a static "חושב…", it
// walks through phases and shows elapsed time + a soft estimate, so a long
// large-document answer feels alive rather than stuck. The backend returns the
// whole answer in one (non-streaming) call, so phases are time-based —
// calibrated to typical large-context latency (gemini ~30-40s on a big Hebrew
// doc). Document-scoped chats get document-flavored phases + an ETA hint;
// general chats get a lighter, shorter sequence.
const DOC_PHASES: { at: number; label: string }[] = [
  { at: 0, label: 'קורא את המסמכים' },
  { at: 4, label: 'מנתח את התוכן' },
  { at: 12, label: 'מאתר נתונים רלוונטיים' },
  { at: 24, label: 'מנסח תשובה' },
  { at: 45, label: 'כמעט שם — מסמך גדול לוקח קצת יותר' },
];
const GENERAL_PHASES: { at: number; label: string }[] = [
  { at: 0, label: 'חושב' },
  { at: 6, label: 'מנסח תשובה' },
  { at: 18, label: 'עוד רגע' },
];

const ThinkingIndicator: React.FC<{ hasContext: boolean; domain?: AgentDomain }> = ({ hasContext, domain }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const id = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const phases = hasContext ? DOC_PHASES : GENERAL_PHASES;
  // Latest phase whose threshold has passed.
  const phase = phases.reduce((acc, p) => (elapsed >= p.at ? p : acc), phases[0]);

  // Show elapsed only after a couple of seconds; add a soft ETA for the slow
  // (document) path so the wait feels bounded.
  let hint: string | null = null;
  if (elapsed >= 3) {
    hint = hasContext
      ? `${elapsed} שניות · בדרך כלל עד דקה למסמך גדול`
      : `${elapsed} שניות`;
  }

  return (
    <div className="flex gap-3 items-start animate-message-in motion-reduce:animate-none" dir="rtl">
      <AgentAvatar domain={domain} size={28} className="mt-0.5" />
      <div className="min-w-0 pt-1">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="font-medium">{phase.label}</span>
          <span className="flex gap-1" aria-hidden="true">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-bounce motion-reduce:animate-none" style={{ animationDelay: '0ms', animationDuration: '1s' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-bounce motion-reduce:animate-none" style={{ animationDelay: '150ms', animationDuration: '1s' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-primary-400 animate-bounce motion-reduce:animate-none" style={{ animationDelay: '300ms', animationDuration: '1s' }} />
          </span>
        </div>
        {hint && <div className="mt-0.5 text-xs text-gray-400 tabular-nums">{hint}</div>}
      </div>
    </div>
  );
};

// Upload/OCR progress card. The backend reports a coarse `processing_progress`
// that plateaus during the long OCR step, so a raw bar reads as frozen. Like the
// chat ThinkingIndicator, this eases a *felt* progress forward and walks staged
// labels by elapsed time — never below the real %, capped at 95% until the doc
// is actually PROCESSED — plus an indeterminate shimmer so there's always motion.
// localStorage key for the "נתח כשמאות מקרקעין" chip dismissal (see below).
const SCOPE_ACTION_DISMISSED_KEY = 'chat.scopeActionDismissed';

// The mid/late labels double as consumer messaging: the wait is longer because
// of the security work we do on every document (full Hebrew extraction, PII
// scanning, encryption) — framing the delay as protection, not slowness.
const UPLOAD_PHASES: { at: number; label: string }[] = [
  { at: 0, label: 'מעלה את הקובץ' },
  { at: 3, label: 'מזהה טקסט (OCR)' },
  { at: 12, label: 'מעבד OCR — חילוץ טקסט מלא בעברית…' },
  { at: 24, label: '🔒 סורק מידע אישי (PII) ומצפין — שומרים על המידע שלך' },
  { at: 40, label: 'מנתח מבנה ונתונים' },
  { at: 58, label: '🔒 בדיקות האבטחה מוסיפות מעט זמן — המטרה להגן על המסמך שלך' },
  { at: 85, label: 'כמעט מוכן — מסמך גדול לוקח קצת יותר' },
];

const UploadProgressRow: React.FC<{
  upload: UploadTracker;
  onDismiss: (documentId: number) => void;
}> = ({ upload, onDismiss }) => {
  const isFailed = upload.status === DocumentStatus.FAILED;
  const isProcessed = upload.status === DocumentStatus.PROCESSED;
  const isDone = isProcessed || isFailed;

  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (isDone) return;
    const start = Date.now();
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    return () => window.clearInterval(id);
  }, [isDone]);

  const realPct = Math.max(0, Math.min(100, upload.progress || 0));
  // Asymptotic creep toward a 95% ceiling; never below the backend's real %.
  const eased = 95 * (1 - Math.exp(-elapsed / 18));
  const displayPct = isProcessed ? 100 : Math.min(95, Math.max(realPct, eased, 6));

  const phase = UPLOAD_PHASES.reduce((acc, p) => (elapsed >= p.at ? p : acc), UPLOAD_PHASES[0]);
  const badge = isFailed ? 'נכשל' : isProcessed ? 'מוכן' : `${Math.round(displayPct)}%`;
  const stageText = isFailed ? upload.error : isProcessed ? null : (upload.error || phase.label);

  const cardBorder = isFailed ? 'border-red-200' : isProcessed ? 'border-emerald-200' : 'border-amber-200';
  const accentColor = isFailed ? 'text-red-500' : isProcessed ? 'text-emerald-500' : 'text-amber-500';
  const badgeClass = isFailed
    ? 'bg-red-100 text-red-700'
    : isProcessed
      ? 'bg-emerald-100 text-emerald-700'
      : 'bg-amber-100 text-amber-700';
  const barColor = isFailed ? 'bg-red-500' : 'bg-amber-500';

  return (
    <div className={`rounded-xl border bg-white/80 px-3 py-2 ${cardBorder}`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${accentColor}`}>
          {isFailed ? (
            <AlertTriangle className="h-4 w-4" />
          ) : isProcessed ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate text-sm font-medium text-gray-800">{upload.filename}</div>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${badgeClass}`}>
              {badge}
            </span>
          </div>
          {stageText && (
            <div className="mt-0.5 text-xs text-gray-500">{stageText}</div>
          )}
          {!isProcessed && (
            <div className="relative mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-full rounded-full ${barColor} transition-[width] duration-500 ease-out`}
                style={{ width: `${displayPct}%` }}
              />
              {!isFailed && (
                <div className="pointer-events-none absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
              )}
            </div>
          )}
        </div>
        {isDone && (
          <button
            onClick={() => onDismiss(upload.documentId)}
            className="text-gray-400 transition-colors hover:text-gray-600"
            title="הסתר"
            aria-label="הסתר את כרטיס ההעלאה"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Flat message row (Claude/GPT style):
//  • user      — a subtle gray rounded block aligned to the start (right in RTL)
//  • assistant — full-width flowing markdown next to a small avatar, no bubble
export interface ReportFindingsApplyResult {
  matched: number;
  updated: number;
  review: number;
  unresolved: number;
  pending: number;
  itemNames: string[];
}

const reportFindingsResultText = (result: ReportFindingsApplyResult): string => {
  if (result.matched === 0) return 'לא זוהו בתשובה סעיפים פתוחים מתיק ההשלמה.';
  const parts: string[] = [];
  if (result.updated > 0) parts.push(`${result.updated} עודכנו אוטומטית`);
  if (result.review > 0) parts.push(`${result.review} ממתינים לאישור`);
  if (result.unresolved > 0) parts.push(`${result.unresolved} עדיין לא נמצאו`);
  if (result.pending > 0) parts.push(`${result.pending} ממשיכים ברקע`);
  return parts.length > 0 ? parts.join(' · ') : 'הבדיקה הסתיימה ללא שינוי בסטטוס.';
};

const MessageRow: React.FC<{
  message: ChatMessage;
  isLast?: boolean;
  isStreaming?: boolean;
  sendText?: (text: string) => void;
  domain?: AgentDomain;
  showReportAction?: boolean;
  onApplyReportFindings?: (message: ChatMessage) => Promise<ReportFindingsApplyResult>;
}> = ({
  message,
  isLast,
  isStreaming,
  sendText,
  domain,
  showReportAction = true,
  onApplyReportFindings,
}) => {
  const isUser = message.role === 'user';
  const [isExpanded, setIsExpanded] = useState(false);
  const [applyStatus, setApplyStatus] = useState<'idle' | 'running' | 'success' | 'error'>('idle');
  const [applyResult, setApplyResult] = useState<ReportFindingsApplyResult | null>(null);
  const [applyError, setApplyError] = useState('');
  const { openArtifact } = useArtifactsPanel();

  const applyFindings = async () => {
    if (!onApplyReportFindings || applyStatus === 'running') return;
    setApplyStatus('running');
    setApplyError('');
    try {
      const result = await onApplyReportFindings(message);
      setApplyResult(result);
      setApplyStatus('success');
    } catch (error) {
      setApplyError(error instanceof Error ? error.message : 'לא הצלחנו לעדכן את תיק ההשלמה');
      setApplyStatus('error');
    }
  };

  if (isCompactionMessage(message)) {
    return (
      <div className="flex items-center gap-3 my-3 text-[11px] text-gray-400 select-none" dir="rtl">
        <div className="h-px flex-1 bg-gray-200" />
        <span>{message.content}</span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>
    );
  }

  // Assistant messages may carry a fenced ```ask block with clarifying
  // questions: parse it out so the visible markdown is clean and we can render
  // interactive option chips below the answer.
  const { cleaned, clarify } = isUser
    ? { cleaned: message.content, clarify: null }
    : parseClarifyBlock(message.content);

  // Collapse very long assistant messages (or those carrying artifacts).
  const isLong = !isUser && (
    cleaned.length > 1200 ||
    (message.artifacts && message.artifacts.length > 0)
  );
  const collapsed = isLong && !isExpanded;

  if (isUser) {
    return (
      <div className="flex justify-start animate-message-in motion-reduce:animate-none" dir="rtl">
        <div className="max-w-[85%] rounded-2xl bg-gray-100 px-4 py-2.5 text-[15px] leading-relaxed text-gray-900">
          <div className="whitespace-pre-wrap">{message.content}</div>
          {message.fileContext && message.fileContext.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-gray-200/70">
              {message.fileContext.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs text-gray-500 bg-white rounded px-1.5 py-0.5">
                  <FileText className="h-3 w-3" />
                  {f}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3 items-start animate-message-in motion-reduce:animate-none" dir="rtl">
      <AgentAvatar domain={domain} size={28} className="mt-0.5" />
      <div className="min-w-0 flex-1 text-[15px] leading-relaxed text-gray-800">
        <div className={`relative ${collapsed ? 'max-h-[480px] overflow-hidden' : ''}`}>
          <MarkdownRenderer content={cleaned} />
          {message.artifacts && message.artifacts.length > 0 && (
            <div className="mt-3 space-y-2">
              {message.artifacts.map((artifact) => (
                <ArtifactRenderer key={artifact.id} artifact={artifact} />
              ))}
            </div>
          )}
          {collapsed && (
            <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-white to-transparent" />
          )}
        </div>
        {/* Outside the collapsible wrapper so the actionable chips are never clipped. */}
        {clarify && sendText && (
          <ClarifyQuestions
            spec={clarify}
            disabled={!isLast || isStreaming}
            onSubmit={sendText}
          />
        )}
        {isLong && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
          >
            <ArrowDown className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            {isExpanded ? 'הצג פחות' : 'הצג את כל הניתוח'}
          </button>
        )}
        {showReportAction && !message.isStreaming && (cleaned || '').trim().length > 0 && (
          <div className="mt-2 flex items-center gap-3">
            <button
              onClick={() =>
                openArtifact({
                  kind: 'report',
                  title: deriveReportTitle(cleaned || ''),
                  markdown: cleaned || '',
                  sourceMessageId: message.id,
                })
              }
              className="text-xs text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
              title="פתח את התשובה כדוח לייצוא"
            >
              <FileText className="h-3.5 w-3.5" />
              פתח כדוח
            </button>
          </div>
        )}
        {onApplyReportFindings && message.role === 'assistant' && !message.isStreaming && (cleaned || '').trim().length > 0 && (
          <div className="mt-3 border-t border-gray-100 pt-3" aria-live="polite">
            {applyStatus !== 'success' && (
              <button
                type="button"
                onClick={() => void applyFindings()}
                disabled={applyStatus === 'running'}
                className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800 transition-colors hover:border-emerald-300 hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-wait disabled:opacity-70"
              >
                {applyStatus === 'running'
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  : <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />}
                {applyStatus === 'running' ? 'מאמת מול הדוח ומעדכן…' : 'אמת והעבר לתיק ההשלמה'}
              </button>
            )}
            {applyStatus === 'idle' && (
              <p className="mt-1.5 text-[11px] leading-5 text-gray-500">
                המספרים יחולצו וייבדקו שוב מול המקור לפני שינוי הסטטוס.
              </p>
            )}
            {applyStatus === 'success' && applyResult && (
              <div className={`rounded-lg border px-3 py-2 text-xs leading-5 ${applyResult.updated > 0 || applyResult.review > 0 ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-gray-200 bg-gray-50 text-gray-700'}`} role="status">
                <div className="flex items-start gap-2 font-medium">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span>{reportFindingsResultText(applyResult)}</span>
                </div>
                {applyResult.itemNames.length > 0 && (
                  <p className="mt-1 text-[11px] opacity-80" title={applyResult.itemNames.join(' · ')}>
                    נבדקו: {applyResult.itemNames.slice(0, 3).join(' · ')}{applyResult.itemNames.length > 3 ? ` ועוד ${applyResult.itemNames.length - 3}` : ''}
                  </p>
                )}
              </div>
            )}
            {applyStatus === 'error' && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs leading-5 text-red-800" role="alert">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>{applyError}</span>
              </div>
            )}
          </div>
        )}
        {message.isStreaming && (
          <span className="inline-block w-1.5 h-4 bg-current animate-streaming-cursor align-text-bottom mr-0.5" />
        )}
      </div>
    </div>
  );
};

interface ChatActiveProject {
  id: string;
  name: string;
  document_count: number;
}

interface ChatAreaProps {
  activeProjectId?: string | null;
  activeProject?: ChatActiveProject | null;
  onClearProject?: () => void;
  /** Refresh project list + counts (shared with AppShell). Called after the
      in-composer upload completes so the sidebar badge updates immediately. */
  onProjectsRefresh?: () => void | Promise<void>;
  /** Compact mode used inside a report-side drawer. It keeps the existing chat
      engine while removing controls that would let the user escape the report
      scope (uploads, tools and recipe switching). */
  variant?: 'full' | 'contextual';
  contextKey?: string;
  contextDocument?: { id: number; name: string };
  initialRecipeId?: string;
  usageCategory?: ProductUsageCategory;
  lockRecipe?: boolean;
  contextualSuggestions?: string[];
  emptyTitle?: string;
  emptyDescription?: string;
  onApplyReportFindings?: (message: ChatMessage) => Promise<ReportFindingsApplyResult>;
}

// Friendly time-of-day greeting + best-effort display name for the home screen.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function greetingNameFor(user: { username?: string; email?: string } | null | undefined): string {
  if (!user) return '';
  const uname = (user.username || '').trim();
  if (uname && !UUID_RE.test(uname) && !uname.startsWith('w3key_')) return uname;
  const local = (user.email || '').split('@')[0];
  if (local && !local.startsWith('w3key_')) return local;
  return '';
}

const ChatArea: React.FC<ChatAreaProps> = ({
  activeProjectId,
  activeProject,
  onClearProject,
  onProjectsRefresh,
  variant = 'full',
  contextKey,
  contextDocument,
  initialRecipeId,
  usageCategory = 'chat_agent',
  lockRecipe = false,
  contextualSuggestions,
  emptyTitle,
  emptyDescription,
  onApplyReportFindings,
}) => {
  const isContextual = variant === 'contextual';
  const {
    messages,
    addMessage,
    isStreaming,
    setIsStreaming,
    selectedTools,
    setSelectedTools,
    currentSessionId,
    setCurrentSessionId,
    currentSession,
    activeDomain,
    setActiveDomain,
    clearChat,
  } = useChatContext();
  const {
    tree,
    selectedFileIds,
    clearFileSelection,
    refreshTree,
    setTree,
    sessionSourceIds,
    addSessionSource,
    setSessionSources,
    clearSessionSources,
    trackedUploads,
    trackUpload,
    dismissUpload,
  } = useFileTreeContext();
  const { mcpStatus, refreshMcpStatus, user } = useAuthContext();
  const { openArtifact } = useArtifactsPanel();
  const { refresh: refreshQuota } = useQuota();
  const [input, setInput] = useState('');
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [showComposerMenu, setShowComposerMenu] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<'tools' | 'recipes' | 'legal' | null>(null);
  const [isLoadingMcpTools, setIsLoadingMcpTools] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [taseModalOpen, setTaseModalOpen] = useState(false);
  const [activeRecipeId, setActiveRecipeId] = useState<string | null>(initialRecipeId || null);
  const [includeRawOcr, setIncludeRawOcr] = useState<boolean>(false);
  const [recipes, setRecipes] = useState<any[]>([]);
  const [legalSkills, setLegalSkills] = useState<LegalSkill[]>([]);
  const [activeLegalSkillId, setActiveLegalSkillId] = useState<string | null>(null);
  // The "🏠 נתח כשמאות מקרקעין" suggestion chip. Dismissal is persisted to
  // localStorage — plain useState reset on every remount (new chat, route
  // change), so the chip kept resurfacing right after the user closed it.
  const [scopeActionDismissed, setScopeActionDismissed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(SCOPE_ACTION_DISMISSED_KEY) === '1';
    } catch {
      return false; /* localStorage may be blocked (Safari private mode) */
    }
  });
  const dismissScopeAction = () => {
    setScopeActionDismissed(true);
    try {
      window.localStorage.setItem(SCOPE_ACTION_DISMISSED_KEY, '1');
    } catch {
      /* ignore */
    }
  };
  const composerMenuRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Cancels the in-flight chat-job poll loop (Stop button / unmount / superseding send).
  const pollCancelRef = useRef<{ cancelled: boolean } | null>(null);
  const activeContextKeyRef = useRef<string | null>(null);
  useEffect(() => () => { if (pollCancelRef.current) pollCancelRef.current.cancelled = true; }, []);

  // A report drawer owns a single, explicit document scope. Reset only when
  // moving to another report; merely closing/reopening the drawer preserves the
  // conversation because ChatArea remains mounted in ReportsLayout.
  useEffect(() => {
    if (!isContextual || !contextKey || !contextDocument) return;
    if (activeContextKeyRef.current === contextKey) return;

    if (pollCancelRef.current) pollCancelRef.current.cancelled = true;
    activeContextKeyRef.current = contextKey;
    clearChat();
    clearFileSelection();
    setSessionSources([`doc-${contextDocument.id}`]);
    setSelectedTools([]);
    setIsStreaming(false);
    setActiveLegalSkillId(null);
    setIncludeRawOcr(false);
    setActiveRecipeId(initialRecipeId || null);
    setActiveDomain('financial');
    setInput('');
  }, [
    clearChat,
    clearFileSelection,
    contextDocument,
    contextKey,
    initialRecipeId,
    isContextual,
    setActiveDomain,
    setIsStreaming,
    setSelectedTools,
    setSessionSources,
  ]);

  // Session sources are managed by ChatPage on every route change: a loaded
  // chat restores its persisted document context, a new chat clears it. Doing
  // it there (rather than reacting to currentSessionId here) avoids a race that
  // wiped the just-restored sources on session switch.

  // When an existing conversation loads, adopt its bound domain so the avatar,
  // greeting and the `domain` sent with the next message all match it. A new
  // chat carries no session domain → keep whatever the user picked (sidebar).
  useEffect(() => {
    if (currentSession?.domain) {
      setActiveDomain(currentSession.domain as AgentDomain);
    }
  }, [currentSession?.domain, setActiveDomain]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 100);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [messages.length]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    // Claude-style roomy composer: a multi-line minimum (~2.5 lines), growing
    // with content up to a larger cap.
    ta.style.height = Math.min(Math.max(ta.scrollHeight, isContextual ? 48 : 64), 200) + 'px';
  }, [input, isContextual]);

  // Close composer + menu on outside click
  useEffect(() => {
    if (!showComposerMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (composerMenuRef.current && !composerMenuRef.current.contains(e.target as Node)) {
        setShowComposerMenu(false);
        setOpenSubmenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showComposerMenu]);

  // Load recipes list
  useEffect(() => {
    apiService.getRecipes().then(setRecipes).catch(() => {});
  }, []);

  // Load legal skills (W3 legal vertical) for the "בחינה משפטית" menu
  useEffect(() => {
    apiService.getLegalSkills().then(setLegalSkills).catch(() => {});
  }, []);

  // Recipe and legal skill are mutually exclusive — picking one clears the other.
  const selectRecipe = (id: string | null) => {
    if (lockRecipe) return;
    setActiveRecipeId(id);
    if (id) setActiveLegalSkillId(null);
  };
  const selectLegalSkill = (id: string | null) => {
    setActiveLegalSkillId(id);
    if (id) setActiveRecipeId(null);
  };

  // Post an in-conversation message the moment an attached document becomes
  // ready (or fails) — the definitive "אפשר לשאול עכשיו" signal the user asked
  // for. Polling itself lives in FileTreeContext (shared by attach / drop /
  // TASE import); here we only observe status *transitions* for documents that
  // belong to this chat's context.
  const lastUploadStatusesRef = useRef<Map<number, string>>(new Map());
  useEffect(() => {
    const prev = lastUploadStatusesRef.current;
    const next = new Map<number, string>();
    for (const u of trackedUploads) {
      next.set(u.documentId, u.status);
      const before = prev.get(u.documentId);
      if (!before || before === u.status) continue;
      const docKey = `doc-${u.documentId}`;
      const inScope = sessionSourceIds.has(docKey) || selectedFileIds.has(docKey);
      if (!inScope) continue;
      if (u.status === DocumentStatus.PROCESSED) {
        addMessage({
          id: uuidv4(),
          session_id: '',
          role: 'assistant',
          content: `✅ **${u.filename}** מוכן — אפשר לשאול עליו עכשיו.`,
          created_at: new Date().toISOString(),
        });
      } else if (u.status === DocumentStatus.FAILED) {
        addMessage({
          id: uuidv4(),
          session_id: '',
          role: 'assistant',
          content: `⚠️ עיבוד **${u.filename}** נכשל${u.error ? ` (${u.error})` : ''}. אפשר לנסות להעלות אותו שוב.`,
          created_at: new Date().toISOString(),
        });
      }
    }
    lastUploadStatusesRef.current = next;
  }, [trackedUploads, sessionSourceIds, selectedFileIds, addMessage]);

  // File upload handler — supports selecting several files at once (NotebookLM
  // style: attach 3-4 reports, then ask questions across all of them).
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedTypes = ['application/pdf', 'text/html', 'application/xhtml+xml', 'text/xml', 'application/xml', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const allowedExts = ['.pdf', '.html', '.htm', '.xbrl', '.xml', '.csv', '.xlsx', '.docx'];
    const valid: File[] = [];
    for (const file of files) {
      const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
      if (!allowedTypes.includes(file.type) && !allowedExts.includes(ext)) {
        toast.error(`"${file.name}" — סוג קובץ לא נתמך (PDF / HTML / XBRL / CSV / XLSX)`);
      } else if (file.size > 50 * 1024 * 1024) {
        toast.error(`"${file.name}" חורג מ-50MB`);
      } else {
        valid.push(file);
      }
    }
    if (valid.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    const uploadedNames: string[] = [];
    const readyNames: string[] = [];
    for (const file of valid) {
      const tempNodeId = `temp-${uuidv4()}`;
      const tempNode: FileNode = {
        id: tempNodeId,
        name: file.name,
        type: 'file',
        parentId: 'org-root',
        document: {
          id: -1,
          filename: file.name,
          original_filename: file.name,
          file_size: file.size,
          mime_type: file.type,
          status: DocumentStatus.UPLOADED,
          owner_id: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          processing_progress: 2,
          processing_stage: 'מעלה את הקובץ',
          can_delete: true,
          access_scope: 'owned',
        },
      };
      setTree((prev) => addTempFileNode(prev, tempNode));
      try {
        const uploadResponse = await apiService.uploadDocument(file, activeProjectId || undefined);
        trackUpload({
          documentId: uploadResponse.document_id,
          filename: file.name,
          status: uploadResponse.status,
          stage: 'הקובץ הועלה וממתין להתחלת עיבוד',
        });
        // Attach the just-uploaded doc as a *persistent* source of this chat, so
        // it stays in context across turns AND shows in the Sources panel — not a
        // one-shot tree selection that `handleSend` clears after the first send.
        // The node id format `doc-<id>` matches `_normalize_document_ids` on the
        // backend (and `_extract_w3_job_id_from_document` resolves its vault id).
        addSessionSource(`doc-${uploadResponse.document_id}`);
        if (String(uploadResponse.status) === DocumentStatus.PROCESSED) {
          readyNames.push(file.name);
        } else {
          uploadedNames.push(file.name);
        }
      } catch (err: any) {
        const detail = err?.response?.data?.detail;
        const message = Array.isArray(detail) ? detail[0]?.msg : detail;
        toast.error(message ? `"${file.name}": ${message}` : `שגיאה בהעלאת "${file.name}"`);
      } finally {
        setTree((prev) => removeNodeById(prev, tempNodeId));
      }
    }

    if (uploadedNames.length > 0 || readyNames.length > 0) {
      // Honest status message: a document is NOT ready for questions until OCR
      // finishes. The upload card above the composer tracks progress, and a
      // follow-up "✅ מוכן" message lands when processing completes.
      const bold = (names: string[]) => names.map((n) => `**${n}**`).join(', ');
      const projectSuffix = activeProject ? ` ושויכו לפרויקט **${activeProject.name}**` : '';
      const parts: string[] = [];
      if (readyNames.length > 0) {
        parts.push(`📄 ${bold(readyNames)} ${readyNames.length === 1 ? 'מוכן' : 'מוכנים'} — אפשר לשאול עליו${readyNames.length === 1 ? '' : 'הם'} כבר עכשיו.`);
      }
      if (uploadedNames.length > 0) {
        parts.push(
          uploadedNames.length === 1
            ? `📄 הקובץ ${bold(uploadedNames)} הועלה${projectSuffix} ונכנס לעיבוד (חילוץ טקסט ו-OCR). אעדכן כאן כשיהיה מוכן לשאלות — אפשר לעקוב אחרי ההתקדמות מעל תיבת הכתיבה.`
            : `📄 ${uploadedNames.length} קבצים הועלו${projectSuffix} ונכנסו לעיבוד: ${bold(uploadedNames)}. אעדכן כאן על כל קובץ כשיהיה מוכן לשאלות.`,
        );
      }
      addMessage({
        id: uuidv4(),
        session_id: '',
        role: 'assistant',
        content: parts.join('\n\n'),
        created_at: new Date().toISOString(),
      });
      refreshTree();
      // Bump the per-project document count in the sidebar / projects index.
      onProjectsRefresh?.();
    }

    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Tool toggle handler
  const toggleTool = (tool: Tool) => {
    const isSelected = selectedTools.some(t => t.id === tool.id);
    if (isSelected) {
      setSelectedTools(selectedTools.filter(t => t.id !== tool.id));
    } else {
      setSelectedTools([...selectedTools, tool]);
    }
  };

  // Always the curated mode list — never the raw MCP catalog. The live
  // `mcpStatus` is still used for the connectivity badge below.
  const availableTools: Tool[] = USER_TOOLS;

  const handleComposerMenuToggle = async () => {
    // Refresh the MCP tool catalog when opening the menu so the latest set
    // of plugins / financial tasks shows up next to the file-attach action.
    if (!showComposerMenu) {
      setIsLoadingMcpTools(true);
      try {
        await refreshMcpStatus();
      } catch {
        toast.error('לא ניתן לטעון כלים מה-MCP כרגע');
      } finally {
        setIsLoadingMcpTools(false);
      }
    }
    if (showComposerMenu) setOpenSubmenu(null);
    setShowComposerMenu(!showComposerMenu);
  };

  const closeComposerMenu = () => {
    setShowComposerMenu(false);
    setOpenSubmenu(null);
  };

  const handleSend = () => {
    let text = input.trim();
    // A legal skill runs a review ON the selected document, so allow sending with
    // no typed question — substitute a visible default so the user bubble is
    // meaningful and the backend receives a real user_message. (With no document
    // the backend still returns the Hebrew "select a document" guard.)
    if (!text && activeLegalSkillId) {
      text = 'בצע בחינה משפטית מקיפה של המסמך';
    }
    if (!text || isStreaming) return;
    setInput('');
    return sendText(text);
  };

  // Core send pipeline, parameterised on the message text. The textarea path
  // (handleSend) clears the input then delegates here; clarifying-question chips
  // call sendText directly with the chosen answer (no textarea involvement).
  const sendText = async (text: string) => {
    if (!text || isStreaming) return;

    // Effective context = the chat's persistent sources + any ad-hoc tree picks.
    const contextFiles = Array.from(
      new Set<string>(Array.from(sessionSourceIds).concat(Array.from(selectedFileIds))),
    );
    const nodeLabels = mergeUploadLabels(buildNodeLabelMap(tree), trackedUploads);
    if (contextDocument) nodeLabels.set(`doc-${contextDocument.id}`, contextDocument.name);
    const contextFileLabels = contextFiles.map((fileId) => nodeLabels.get(fileId) || fileId);

    const userMsg: ChatMessage = {
      id: uuidv4(),
      session_id: currentSessionId || '',
      role: 'user',
      content: text,
      fileContext: contextFileLabels.length > 0 ? contextFileLabels : undefined,
      created_at: new Date().toISOString(),
    };

    addMessage(userMsg);

    // Opinion skills (senior-opinion / internal-review) run a long best-of-N
    // gemini-2.5-pro generation that outlasts the proxy/ALB idle timeout. So,
    // like chat, they're async: the POST returns 202 + job_id, and we poll
    // /recipes/jobs/{id} (apiService.getRecipeJob) until done. Shared helper for
    // both branches below.
    const runOpinionSkill = async (
      submit: (docId: number) => Promise<RecipeJobAccepted>,
      messages: { needsOneDoc: string; failed: string; expired: string },
    ) => {
      const docIds = contextFiles
        .map((fid) => parseInt(String(fid).replace('doc-', ''), 10))
        .filter((n) => Number.isFinite(n));
      const labels = contextFileLabels;
      clearFileSelection();

      const assistant = (content: string, withContext = false) => addMessage({
        id: uuidv4(),
        session_id: withContext ? (currentSessionId || '') : '',
        role: 'assistant',
        content,
        fileContext: withContext && labels.length > 0 ? labels : undefined,
        created_at: new Date().toISOString(),
      });

      if (docIds.length !== 1) {
        assistant(messages.needsOneDoc);
        return;
      }

      setIsStreaming(true);
      try {
        const { job_id } = await submit(docIds[0]);  // 202 + job_id (fast)

        // Poll until the background generation resolves. Fresh cancel token so
        // Stop / unmount / a superseding send cancels just this loop.
        const token = { cancelled: false };
        pollCancelRef.current = token;
        const POLL_MS = 2000;
        const CEILING_MS = 300000;  // 5-min client ceiling for the long best-of-N
        const startedAt = Date.now();

        while (!token.cancelled) {
          if (Date.now() - startedAt > CEILING_MS) { assistant(messages.failed); break; }
          await new Promise(r => setTimeout(r, POLL_MS));
          if (token.cancelled) break;

          let job: RecipeJobStatus;
          try {
            job = await apiService.getRecipeJob(job_id);
          } catch (e: any) {
            if (e?.response?.status === 404) { assistant(messages.failed); break; } // TTL-expired / lost
            throw e; // real network/auth error → outer catch
          }

          if (job.status === 'done') { assistant(job.content || messages.failed, true); refreshQuota(); break; }
          if (job.status === 'error') { assistant(messages.failed); break; }
          // status === 'pending' → keep polling; ThinkingIndicator keeps animating
        }
      } catch (err: any) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        let msg = messages.failed;
        if (status === 422) {
          msg = typeof detail === 'string' ? detail : messages.expired;
        } else if (status === 401) {
          msg = '🔑 פג תוקף ההתחברות. נסה להתחבר מחדש.';
        }
        assistant(msg);
      } finally {
        setIsStreaming(false);
      }
    };

    // These opinion-skill branches run before the legal/normal send path below.
    // They're safe to check first because selectRecipe/selectLegalSkill keep
    // activeRecipeId and activeLegalSkillId mutually exclusive — a legal skill is
    // never active here. If that invariant is ever broken, a legal+opinion combo
    // would silently take the opinion path; keep the exclusivity helpers intact.
    // The typed message doubles as guidance/rerun-corrections for the skill —
    // it flows to the backend as user_notes and is injected into the
    // generation prompt (capped to the backend's 4000-char validation limit).
    const opinionNotes = text.trim().slice(0, 4000) || undefined;

    if (activeRecipeId === 'senior_appraiser_opinion') {
      await runOpinionSkill(
        (docId) => apiService.generateSeniorOpinion(docId, undefined, opinionNotes),
        {
          needsOneDoc: 'לחוות דעת שמאי בכיר יש לבחור מסמך אחד (דוח אפס / שומת מקרקעין) שעובד דרך W3.',
          failed: 'לא ניתן היה להפיק חוות דעת כעת. נסה שוב בעוד מספר רגעים.',
          expired: 'המסמך טרם עובד דרך W3. יש לעבד אותו לפני הפקת חוות דעת שמאי בכיר.',
        },
      );
      return;
    }

    if (activeRecipeId === 'internal_appraisal_review') {
      await runOpinionSkill(
        (docId) => apiService.generateInternalReview(docId, undefined, opinionNotes),
        {
          needsOneDoc: 'להתייחסות פנימית לשמאות יש לבחור מסמך אחד (שומת מקרקעין / דוח אפס) שעובד דרך W3.',
          failed: 'לא ניתן היה להפיק התייחסות פנימית כעת. נסה שוב בעוד מספר רגעים.',
          expired: 'המסמך טרם עובד דרך W3. יש לעבד אותו לפני הפקת התייחסות פנימית לשמאות.',
        },
      );
      return;
    }

    clearFileSelection();
    setIsStreaming(true);

    // Auto-open the matching artifact panel when the user asks for valuation/
    // underwriting/graph by name. Frontend-only intent detection — see
    // [detectArtifactIntent](../../utils/detectArtifactIntent.ts). Backend
    // intent tagging in chat responses can replace this later.
    const intent = detectArtifactIntent(text, activeProjectId ?? null);
    if (intent && !isContextual) openArtifact(intent);

    try {
      const selectedToolIds = selectedTools.map(tool => tool.id);
      const { job_id, session_id } = await apiService.sendChatMessage(
        text,
        contextFiles.length > 0 ? contextFiles : undefined,
        currentSessionId || undefined,
        selectedToolIds.length > 0 ? selectedToolIds : undefined,
        activeRecipeId || undefined,
        activeProjectId || undefined,
        includeRawOcr || undefined,
        activeLegalSkillId || undefined,
        activeDomain,
        usageCategory,
      );
      if (session_id) setCurrentSessionId(session_id);

      // Poll the job until it resolves. A fresh token per send so the Stop
      // button / unmount / a superseding send can cancel just this loop.
      const token = { cancelled: false };
      pollCancelRef.current = token;
      const POLL_MS = 2000;
      const CEILING_MS = 180000; // 3-minute client ceiling for a lost/stuck job
      const startedAt = Date.now();

      const stuckMessage = () => addMessage({
        id: uuidv4(), session_id: '', role: 'assistant',
        content: 'התשובה נתקעה. נסה לשאול שוב.', created_at: new Date().toISOString(),
      });

      while (!token.cancelled) {
        if (Date.now() - startedAt > CEILING_MS) {
          stuckMessage();
          break;
        }
        await new Promise(r => setTimeout(r, POLL_MS));
        if (token.cancelled) break;

        let job;
        try {
          job = await apiService.getChatJob(job_id);
        } catch (e: any) {
          if (e?.response?.status === 404) { stuckMessage(); break; } // TTL-expired / lost job
          throw e; // real network/auth error → outer catch
        }

        if (job.status === 'done' && job.message) {
          if (job.notice) addMessage(job.notice);
          addMessage({
            ...job.message,
            id: job.message.id || uuidv4(),
            role: 'assistant',
            fileContext: contextFileLabels.length > 0 ? contextFileLabels : job.message.fileContext,
          });
          refreshQuota();
          break;
        }
        if (job.status === 'error') {
          addMessage({
            id: uuidv4(), session_id: '', role: 'assistant',
            content: '⚠️ שגיאה בעיבוד ההודעה. נסה שוב.', created_at: new Date().toISOString(),
          });
          break;
        }
        // status === 'pending' → keep polling; ThinkingIndicator keeps animating
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number }; code?: string; message?: string; _redirecting?: boolean };
      // If the 401 interceptor is already redirecting to login, don't show error
      if ((axiosErr as any)?._redirecting) return;
      const status = axiosErr?.response?.status;
      const code = axiosErr?.code;
      let errorMsg = 'שגיאה בשליחת ההודעה. נסה שוב.';
      if (code === 'ECONNABORTED' || code === 'ERR_NETWORK') {
        errorMsg = '⏱️ הבקשה ארכה זמן רב מדי או שאין חיבור לשרת. נסה שוב.';
      } else if (status === 401) {
        errorMsg = '🔑 פג תוקף ההתחברות. נסה להתחבר מחדש.';
      } else if (status && status >= 500) {
        errorMsg = '⚠️ שגיאה בשרת. נסה שוב בעוד רגע.';
      }
      console.error('Chat send error:', { status, code, message: axiosErr?.message });
      addMessage({
        id: uuidv4(),
        session_id: '',
        role: 'assistant',
        content: errorMsg,
        created_at: new Date().toISOString(),
      });
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // The chat's effective context = persistent session sources + ad-hoc picks.
  const effectiveContextIds = new Set<string>(
    Array.from(sessionSourceIds).concat(Array.from(selectedFileIds)),
  );
  const contextFileCount = effectiveContextIds.size;
  const clearAllContext = () => { clearFileSelection(); clearSessionSources(); };
  const greetingName = greetingNameFor(user);
  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? 'בוקר טוב' : hour < 18 ? 'צהריים טובים' : 'ערב טוב';
  const greeting = greetingName ? `${timeGreeting}, ${greetingName}` : 'במה אפשר לעזור?';
  // The active conversation's persona introduces itself by name + specialty on
  // the home screen.
  const agentMeta = personaForDomain(activeDomain);

  const suggestions: string[] = contextualSuggestions?.length
    ? contextualSuggestions.slice(0, 4)
    : activeRecipeId
      ? (recipes.find((r) => r.id === activeRecipeId)?.follow_up_examples || []).slice(0, 3)
      : ['נתח דוח רווח והפסד', 'סכם דוח שנתי', 'השווה בין רבעונים'];

  // ── Contextual strips that sit above the composer (file context, tools,
  // recipe, upload progress). Defined once, rendered in both the home and
  // active layouts. ──
  // The document/project context is shown by the scope chip below (with its own
  // clear button), so a separate "N files selected" bar would be redundant. The
  // one unique affordance there — "analyse as a real-estate appraisal" — is
  // surfaced inside the scope chip instead (see `scopeAction`).
  const scopeAction =
    contextFileCount > 0 &&
    !scopeActionDismissed &&
    !activeLegalSkillId &&  // don't push appraisal while a legal review is active
    recipes.some((r) => r.id === 'real_estate_appraisal') &&
    activeRecipeId !== 'real_estate_appraisal' ? (
      <span className="inline-flex items-center rounded-full border border-emerald-200 bg-white text-[11px] font-medium text-emerald-700">
        <button
          onClick={() => selectRecipe('real_estate_appraisal')}
          className="inline-flex items-center gap-1 rounded-r-full pl-2 pr-2 py-0.5 hover:bg-emerald-50 transition-colors"
          title="הפעל מתכון לניתוח שמאות מקרקעין"
        >
          🏠 נתח כשמאות מקרקעין
        </button>
        <button
          onClick={dismissScopeAction}
          className="pl-1.5 pr-1 py-0.5 text-emerald-400 hover:text-emerald-700 transition-colors"
          title="הסתר הצעה"
          aria-label="הסתר הצעה"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    ) : null;

  const contextBars = (
    <>
      {/* Selected tools bar */}
      {selectedTools.length > 0 && (
        <div className="px-4 py-1.5 bg-indigo-50 border-t border-indigo-100 flex items-center gap-2 text-sm flex-wrap">
          <Wrench className="h-4 w-4 text-indigo-500 flex-shrink-0" />
          {selectedTools.map(tool => (
            <span key={tool.id} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 rounded-full px-2.5 py-0.5 text-xs font-medium">
              {tool.icon && toolIcons[tool.icon]}
              {tool.name}
              <button onClick={() => toggleTool(tool)} className="hover:text-indigo-900">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Active recipe bar */}
      {activeRecipeId && (
        <div className="px-4 py-1.5 bg-emerald-50 border-t border-emerald-100 flex items-center gap-2 text-sm">
          <BookOpen className="h-4 w-4 text-emerald-500" />
          <span className="text-emerald-700 font-medium">
            {recipes.find(r => r.id === activeRecipeId)?.name_he || 'מתכון פעיל'}
          </span>
          {!lockRecipe && (
            <button onClick={() => setActiveRecipeId(null)} className="mr-auto text-emerald-400 hover:text-emerald-600">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Active legal-skill bar */}
      {activeLegalSkillId && (
        <div className="px-4 py-1.5 bg-indigo-50 border-t border-indigo-100 flex items-center gap-2 text-sm">
          <Scale className="h-4 w-4 text-indigo-500" />
          <span className="text-indigo-700 font-medium">
            {legalSkills.find(s => s.id === activeLegalSkillId)?.name_he || 'בחינה משפטית'}
          </span>
          <button onClick={() => setActiveLegalSkillId(null)} className="mr-auto text-indigo-400 hover:text-indigo-600">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {(() => {
        // Show uploads that still need attention — in-flight or failed — plus a
        // just-completed one for its brief dwell (measured from processedAt), so
        // every upload shows "מעבד… → מוכן" before it fades (the auto-dismiss
        // effect marks it dismissed once the dwell elapses; the entry itself is
        // kept for filename lookups). A long-completed PROCESSED doc is already
        // represented by the scope chip + Sources panel, so no persistent green
        // strip lingers here.
        const now = Date.now();
        const visibleUploads = trackedUploads.filter(
          (u) =>
            !u.dismissed &&
            (u.status !== DocumentStatus.PROCESSED ||
              (u.processedAt != null && now - u.processedAt < UPLOAD_DONE_DWELL_MS)),
        );
        if (visibleUploads.length === 0) return null;
        const hasFailed = visibleUploads.some((u) => u.status === DocumentStatus.FAILED);
        const stripClass = hasFailed
          ? 'border-red-100 bg-red-50/70'
          : 'border-amber-100 bg-amber-50/70';

        return (
        <div className={`border-t px-4 py-2 ${stripClass}`}>
          <div className="max-w-3xl mx-auto space-y-2">
            {visibleUploads.map((upload) => (
              <UploadProgressRow
                key={upload.documentId}
                upload={upload}
                onDismiss={dismissUpload}
              />
            ))}
          </div>
        </div>
        );
      })()}
    </>
  );

  // How many of the chat's attached documents are still being processed —
  // surfaced on the scope chip so it's obvious the answer won't yet be based
  // on those documents.
  const processingInScopeCount = trackedUploads.filter(
    (u) =>
      (u.status === DocumentStatus.UPLOADED || u.status === DocumentStatus.PROCESSING) &&
      effectiveContextIds.has(`doc-${u.documentId}`),
  ).length;

  const contextualNodeLabels = mergeUploadLabels(buildNodeLabelMap(tree), trackedUploads);
  if (contextDocument) contextualNodeLabels.set(`doc-${contextDocument.id}`, contextDocument.name);
  const scopeChipNode = (
    <ScopeChip
      selectedFileIds={effectiveContextIds}
      nodeLabels={contextualNodeLabels}
      activeProject={activeProject || null}
      activeProjectId={activeProjectId ?? null}
      onClearFiles={clearAllContext}
      onClearProject={onClearProject}
      extraAction={scopeAction}
      processingCount={processingInScopeCount}
    />
  );

  // ── Unified Claude/GPT-style composer: `+` menu, textarea, send — all inside
  // one rounded container. Rendered once and docked at the bottom in both the
  // home/empty state and an active conversation. ──
  const composerInner = (
    <>
      {/* Hidden file input */}
      {!isContextual && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.html,.htm,.xbrl,.xml,.csv,.xlsx,.docx"
          onChange={handleFileUpload}
          className="hidden"
        />
      )}

      {/* Import-from-TASE modal. onImported mirrors the post-upload flow so an
          imported filing behaves exactly like an uploaded file: progress card,
          persistent chat source, tree refresh. */}
      {!isContextual && <TaseImportModal
        isOpen={taseModalOpen}
        onClose={() => setTaseModalOpen(false)}
        projectId={activeProjectId || undefined}
        onImported={(documentId, filename) => {
          trackUpload({
            documentId,
            filename,
            status: DocumentStatus.UPLOADED,
            stage: 'הדוח יובא מאתר הבורסה וממתין לעיבוד',
          });
          addSessionSource(`doc-${documentId}`);
          addMessage({
            id: uuidv4(),
            session_id: '',
            role: 'assistant',
            content: `🏛️ הדוח **${filename}** יובא מאתר הבורסה ונכנס לעיבוד. כשיסתיים העיבוד אפשר לשאול עליו — הוא כבר מצורף לשיחה.`,
            created_at: new Date().toISOString(),
          });
          refreshTree();
          onProjectsRefresh?.();
        }}
      />}

      <div className="flex items-end gap-1.5 rounded-2xl border border-gray-300 bg-white px-2 py-1.5 shadow-sm transition-colors focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-500/15">
        {/* `+` button — Claude-style unified composer menu.
            One affordance, one popover for attach / tools / recipe / raw-OCR. */}
        {!isContextual && (() => {
          const extrasCount =
            selectedTools.length + (activeRecipeId ? 1 : 0) + (activeLegalSkillId ? 1 : 0) + (includeRawOcr ? 1 : 0);
          return (
            <div className="relative" ref={composerMenuRef}>
              <button
                onClick={handleComposerMenuToggle}
                disabled={isUploading}
                className={`relative p-2 rounded-lg press flex-shrink-0 disabled:active:scale-100 ${
                  isUploading
                    ? 'text-primary-500 bg-primary-50'
                    : showComposerMenu || extrasCount > 0
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title="הוסף לשיחה — קובץ, כלים, מתכון"
                aria-label="פתח תפריט הוספה"
                aria-expanded={showComposerMenu}
              >
                {isUploading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
                ) : (
                  <Plus className="h-5 w-5" />
                )}
                {extrasCount > 0 && !showComposerMenu && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {extrasCount}
                  </span>
                )}
              </button>

              {showComposerMenu && (
                <div className="absolute bottom-full mb-2 right-0 w-72 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50 max-h-[75vh] overflow-y-auto overflow-x-hidden overscroll-contain origin-bottom-right animate-chat-bubble motion-reduce:animate-none" dir="rtl">
                  {/* Attach file — direct action */}
                  <button
                    onClick={() => { fileInputRef.current?.click(); closeComposerMenu(); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <span className="flex-shrink-0 text-primary-600"><Paperclip className="h-4 w-4" /></span>
                    <div className="flex-1 min-w-0 text-right">
                      <div className="font-medium">העלאת קבצים</div>
                      <div className="text-xs text-gray-500">אפשר לבחור כמה קבצים יחד · PDF · HTML · XBRL · CSV · XLSX · עד 50MB</div>
                    </div>
                  </button>

                  {/* Import a public-company filing straight from Maya (TASE) */}
                  <button
                    onClick={() => { setTaseModalOpen(true); closeComposerMenu(); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <span className="flex-shrink-0 text-primary-600"><Landmark className="h-4 w-4" /></span>
                    <div className="flex-1 min-w-0 text-right">
                      <div className="font-medium">ייבוא מהבורסה</div>
                      <div className="text-xs text-gray-500">חיפוש חברה ← דוח ממאיה ישירות לניתוח</div>
                    </div>
                  </button>

                  <div className="my-1 border-t border-gray-100" />

                  {/* כלים — collapsible inline section of curated analysis modes.
                      Inline (not a sideways flyout) so it can never overlap the
                      composer; the popover scrolls if it grows. */}
                  <div>
                    <button
                      onClick={() => setOpenSubmenu((s) => (s === 'tools' ? null : 'tools'))}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 transition-colors ${
                        openSubmenu === 'tools' ? 'bg-gray-50' : 'hover:bg-gray-50'
                      }`}
                      aria-haspopup="true"
                      aria-expanded={openSubmenu === 'tools'}
                    >
                      <span className="flex-shrink-0 text-indigo-500"><Wrench className="h-4 w-4" /></span>
                      <div className="flex-1 min-w-0 text-right">
                        <div className="font-medium">כלים</div>
                        <div className="text-xs text-gray-500">
                          {selectedTools.length > 0 ? `${selectedTools.length} מצבים פעילים` : 'בחר מצב ניתוח'}
                        </div>
                      </div>
                      {selectedTools.length > 0 && (
                        <span className="flex-shrink-0 w-5 h-5 bg-indigo-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                          {selectedTools.length}
                        </span>
                      )}
                      <ChevronLeft className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${openSubmenu === 'tools' ? '-rotate-90' : ''}`} />
                    </button>

                    {openSubmenu === 'tools' && (
                      <div className="bg-gray-50/60 border-t border-gray-100">
                        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center justify-between">
                          <span>מצבי ניתוח</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            mcpStatus?.connected ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {mcpStatus?.connected ? 'MCP מחובר' : 'MCP לא מחובר'}
                          </span>
                        </div>
                        {isLoadingMcpTools && (
                          <div className="px-3 py-1.5 text-[11px] text-gray-400">בודק חיבור ל-MCP…</div>
                        )}
                        {availableTools.map(tool => {
                          const isActive = selectedTools.some(t => t.id === tool.id);
                          return (
                            <button
                              key={tool.id}
                              onClick={() => toggleTool(tool)}
                              className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-white'
                              }`}
                            >
                              <span className={`flex-shrink-0 ${isActive ? 'text-indigo-500' : 'text-gray-400'}`}>
                                {tool.icon && toolIcons[tool.icon]
                                  ? toolIcons[tool.icon]
                                  : <BarChart3 className="h-4 w-4" />}
                              </span>
                              <div className="flex-1 min-w-0 text-right">
                                <div className="font-medium">{tool.name}</div>
                                {tool.description && (
                                  <div className="text-xs text-gray-500 truncate">{tool.description}</div>
                                )}
                              </div>
                              {isActive && <Check className="h-4 w-4 text-indigo-500 flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* מתכונים — collapsible inline section of recipes */}
                  <div>
                    <button
                      onClick={() => setOpenSubmenu((s) => (s === 'recipes' ? null : 'recipes'))}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 transition-colors ${
                        openSubmenu === 'recipes' ? 'bg-gray-50' : 'hover:bg-gray-50'
                      }`}
                      aria-haspopup="true"
                      aria-expanded={openSubmenu === 'recipes'}
                    >
                      <span className="flex-shrink-0 text-emerald-500"><BookOpen className="h-4 w-4" /></span>
                      <div className="flex-1 min-w-0 text-right">
                        <div className="font-medium">מתכונים</div>
                        <div className="text-xs text-gray-500 truncate">
                          {activeRecipeId
                            ? (recipes.find(r => r.id === activeRecipeId)?.name_he || 'מתכון פעיל')
                            : 'הפעל מתכון ניתוח'}
                        </div>
                      </div>
                      <ChevronLeft className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${openSubmenu === 'recipes' ? '-rotate-90' : ''}`} />
                    </button>

                    {openSubmenu === 'recipes' && (
                      <div className="bg-gray-50/60 border-t border-gray-100">
                        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                          מתכון
                        </div>
                        <button
                          onClick={() => { selectRecipe(null); }}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                            !activeRecipeId ? 'bg-gray-100 text-gray-700' : 'text-gray-600 hover:bg-white'
                          }`}
                        >
                          <span className="flex-shrink-0 text-gray-400"><X className="h-4 w-4" /></span>
                          <div className="flex-1 min-w-0 text-right">
                            <div className="font-medium">ללא מתכון</div>
                            <div className="text-xs text-gray-500">שיחה רגילה</div>
                          </div>
                          {!activeRecipeId && <Check className="h-4 w-4 text-gray-500 flex-shrink-0" />}
                        </button>
                        {recipes.length === 0 && (
                          <div className="px-3 py-2 text-xs text-gray-500">אין מתכונים זמינים</div>
                        )}
                        {recipes.map(recipe => {
                          const isActive = activeRecipeId === recipe.id;
                          return (
                            <button
                              key={recipe.id}
                              onClick={() => selectRecipe(recipe.id)}
                              className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                isActive ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700 hover:bg-white'
                              }`}
                            >
                              <span className={`flex-shrink-0 ${isActive ? 'text-emerald-500' : 'text-gray-400'}`}>
                                <BookOpen className="h-4 w-4" />
                              </span>
                              <div className="flex-1 min-w-0 text-right">
                                <div className="font-medium">{recipe.name_he}</div>
                                {recipe.description_he && (
                                  <div className="text-xs text-gray-500 truncate">{recipe.description_he}</div>
                                )}
                              </div>
                              {isActive && <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* בחינה משפטית — W3 legal-vertical skills */}
                  {legalSkills.length > 0 && (
                  <div>
                    <button
                      onClick={() => setOpenSubmenu((s) => (s === 'legal' ? null : 'legal'))}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 transition-colors ${
                        openSubmenu === 'legal' ? 'bg-gray-50' : 'hover:bg-gray-50'
                      }`}
                      aria-haspopup="true"
                      aria-expanded={openSubmenu === 'legal'}
                    >
                      <span className="flex-shrink-0 text-indigo-500"><Scale className="h-4 w-4" /></span>
                      <div className="flex-1 min-w-0 text-right">
                        <div className="font-medium">בחינה משפטית</div>
                        <div className="text-xs text-gray-500 truncate">
                          {activeLegalSkillId
                            ? (legalSkills.find(s => s.id === activeLegalSkillId)?.name_he || 'בחינה פעילה')
                            : 'בדיקה משפטית של מסמך'}
                        </div>
                      </div>
                      <ChevronLeft className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${openSubmenu === 'legal' ? '-rotate-90' : ''}`} />
                    </button>

                    {openSubmenu === 'legal' && (
                      <div className="bg-gray-50/60 border-t border-gray-100">
                        <button
                          onClick={() => selectLegalSkill(null)}
                          className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                            !activeLegalSkillId ? 'bg-gray-100 text-gray-700' : 'text-gray-600 hover:bg-white'
                          }`}
                        >
                          <span className="flex-shrink-0 text-gray-400"><X className="h-4 w-4" /></span>
                          <div className="flex-1 min-w-0 text-right">
                            <div className="font-medium">ללא בחינה משפטית</div>
                            <div className="text-xs text-gray-500">שיחה רגילה</div>
                          </div>
                          {!activeLegalSkillId && <Check className="h-4 w-4 text-gray-500 flex-shrink-0" />}
                        </button>
                        {legalSkills.map(skill => {
                          const isActive = activeLegalSkillId === skill.id;
                          return (
                            <button
                              key={skill.id}
                              onClick={() => selectLegalSkill(skill.id)}
                              className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-white'
                              }`}
                            >
                              <span className={`flex-shrink-0 ${isActive ? 'text-indigo-500' : 'text-gray-400'}`}>
                                <Scale className="h-4 w-4" />
                              </span>
                              <div className="flex-1 min-w-0 text-right">
                                <div className="font-medium truncate">{skill.name_he}</div>
                                {skill.description && (
                                  <div className="text-xs text-gray-500 truncate">{skill.description}</div>
                                )}
                              </div>
                              {isActive && <Check className="h-4 w-4 text-indigo-500 flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  )}

                  <div className="my-1 border-t border-gray-100" />

                  {/* Raw OCR toggle — direct option */}
                  <button
                    onClick={() => setIncludeRawOcr((v) => !v)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                      includeRawOcr ? 'bg-amber-50 text-amber-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    aria-pressed={includeRawOcr}
                  >
                    <span className={`flex-shrink-0 ${includeRawOcr ? 'text-amber-500' : 'text-gray-400'}`}>
                      <FileSearch className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0 text-right">
                      <div className="font-medium">כלול OCR גולמי</div>
                      <div className="text-xs text-gray-500">מומלץ כשחסרים מספרים מטבלאות</div>
                    </div>
                    {includeRawOcr && <Check className="h-4 w-4 text-amber-500 flex-shrink-0" />}
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={buildPlaceholder({
            activeRecipeId,
            selectedFileCount: effectiveContextIds.size,
            activeProject: activeProject || null,
            activeProjectId: activeProjectId ?? null,
          })}
          dir="rtl"
          rows={2}
          className={`flex-1 resize-none bg-transparent px-1.5 py-2 text-[15px] leading-relaxed text-right text-gray-800 placeholder-gray-400 focus:outline-none max-h-[200px] ${isContextual ? 'min-h-[48px]' : 'min-h-[64px]'}`}
        />

        {isStreaming ? (
          <button
            onClick={() => { setIsStreaming(false); if (pollCancelRef.current) pollCancelRef.current.cancelled = true; }}
            className="p-2 rounded-lg bg-red-500 text-white hover:bg-red-600 press flex-shrink-0"
            title="עצור"
          >
            <StopCircle className="h-5 w-5" />
          </button>
        ) : (
          <button
            onClick={handleSend}
            // An active legal skill runs ON the document, so an empty send is
            // valid (handleSend substitutes a default review prompt) — keep the
            // button enabled so the flow isn't only reachable via the Enter key.
            disabled={!input.trim() && !activeLegalSkillId}
            className="p-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 transition-[transform,background-color,color] duration-150 ease-out-emil active:scale-90 disabled:active:scale-100 flex-shrink-0"
            title="שלח"
          >
            <Send className="h-5 w-5" />
          </button>
        )}
      </div>
    </>
  );

  return (
    // The chat reading canvas carries the user's chosen interface tint
    // (`bg-app` → --app-bg). The composer docks below stay white so they read
    // as a raised input surface over the tinted canvas.
    <div className="relative flex h-full flex-col bg-app" dir="rtl">
      {messages.length === 0 ? (
        // ── Home / empty state — greeting + suggestions scroll above; composer docked at bottom ──
        <>
          <div className="flex-1 overflow-y-auto">
            <div className={`min-h-full flex flex-col items-center justify-center px-4 ${isContextual ? 'py-6' : 'py-10'}`}>
              <div className="w-full max-w-2xl mx-auto flex flex-col items-center">
                <AgentAvatar domain={activeDomain} size={isContextual ? 40 : 56} className="mb-4 shadow-sm" />
                <h1 className={`${isContextual ? 'text-lg' : 'text-2xl'} font-semibold text-gray-800 mb-1 text-center`}>
                  {isContextual ? (emptyTitle || 'מה תרצה לבדוק בדוח?') : greeting}
                </h1>
                <p className={`text-sm text-gray-500 text-center max-w-md ${isContextual ? 'mb-5' : 'mb-6'}`}>
                  {isContextual
                    ? (emptyDescription || 'הדוח והמתכון לניתוח חברה ציבורית כבר מחוברים לשיחה.')
                    : <>היי, אני {agentMeta.name} — {agentMeta.tagline}. אפשר לשאול אותי על הדוחות הכספיים,
                      לבקש ניתוחים, או להעלות מסמכים חדשים.</>}
                </p>

                <div className={isContextual ? 'grid w-full gap-2' : 'flex flex-wrap gap-2 justify-center'}>
                  {suggestions.map((s: string) => (
                    <button
                      key={s}
                      onClick={() => setInput(s)}
                      className={`${isContextual ? 'rounded-xl px-3 py-2.5 text-right leading-5' : 'rounded-full px-3 py-1.5'} bg-white border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Composer dock — pinned to the bottom (same as active conversation) */}
          <div className="border-t border-gray-200 bg-white">
            {!isContextual && contextBars}
            {!isContextual && scopeChipNode}
            <div className={`${isContextual ? 'px-3 pb-3 pt-2' : 'px-4 pt-2 pb-3'}`}>
              <div className="max-w-3xl mx-auto">
                {composerInner}
              </div>
            </div>
          </div>
        </>
      ) : (
        // ── Active conversation — flat messages in a centered column ──
        <>
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
            <div className={`max-w-3xl mx-auto w-full space-y-6 ${isContextual ? 'px-3 py-4' : 'px-4 py-6'}`}>
              {messages.map((msg, idx) => (
                <MessageRow
                  key={msg.id}
                  message={msg}
                  isLast={idx === messages.length - 1}
                  isStreaming={isStreaming}
                  sendText={sendText}
                  domain={activeDomain}
                  showReportAction={!isContextual}
                  onApplyReportFindings={isContextual ? onApplyReportFindings : undefined}
                />
              ))}
              {isStreaming && (
                <ThinkingIndicator hasContext={effectiveContextIds.size > 0 || !!activeProjectId} domain={activeDomain} />
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Scroll to bottom */}
          {showScrollBtn && (
            <button
              onClick={scrollToBottom}
              className="absolute bottom-28 left-1/2 -translate-x-1/2 p-2 bg-white border border-gray-200 rounded-full shadow-lg hover:bg-gray-50 transition-colors z-10"
              aria-label="גלול לתחתית"
            >
              <ArrowDown className="h-4 w-4 text-gray-600" />
            </button>
          )}

          {/* Composer dock */}
          <div className="border-t border-gray-200 bg-white">
            {!isContextual && contextBars}
            {!isContextual && scopeChipNode}
            <div className={`${isContextual ? 'px-3 pb-3 pt-2' : 'px-4 pt-2 pb-3'}`}>
              <div className="max-w-3xl mx-auto">
                {composerInner}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------
// Scope chip — tells the user what the chat is talking *about* right now.
// Document scope wins over project scope because selecting specific files is
// the more deliberate narrowing action.
// ----------------------------------------------------------------------
interface ScopeChipProps {
  selectedFileIds: Set<string>;
  nodeLabels: Map<string, string>;
  activeProject: ChatActiveProject | null;
  activeProjectId: string | null;
  onClearFiles: () => void;
  onClearProject?: () => void;
  extraAction?: React.ReactNode;
  /** Attached documents still in OCR/extraction — shown as a "בעיבוד" badge. */
  processingCount?: number;
}

const ScopeChip: React.FC<ScopeChipProps> = ({
  selectedFileIds,
  nodeLabels,
  activeProject,
  activeProjectId,
  onClearFiles,
  onClearProject,
  extraAction,
  processingCount = 0,
}) => {
  const hasDocs = selectedFileIds.size > 0;
  const hasProject = Boolean(activeProjectId);

  let icon = '💬';
  let label: React.ReactNode = 'שיחה כללית (ללא הקשר של מסמך/פרויקט)';
  let onClear: (() => void) | undefined;
  let clearTitle = '';
  let tone = 'neutral'; // neutral | doc | project
  if (hasDocs) {
    icon = '📄';
    const ids = Array.from(selectedFileIds);
    const names = ids.map((id) => nodeLabels.get(id) || id);
    if (ids.length === 1) {
      label = <>שיחה על מסמך: <span className="font-medium">{names[0]}</span></>;
    } else if (ids.length <= 3) {
      // Small sets read better by name (NotebookLM-style) than as a bare count.
      label = <>שיחה על <span className="font-medium">{names.join(' · ')}</span></>;
    } else {
      label = (
        <>
          שיחה על <span className="font-medium">{ids.length}</span> מסמכים:{' '}
          <span className="font-medium">{names.slice(0, 2).join(' · ')}</span> +{ids.length - 2}
        </>
      );
    }
    onClear = onClearFiles;
    clearTitle = 'נקה בחירת מסמכים';
    tone = 'doc';
  } else if (hasProject) {
    icon = '📁';
    const name = activeProject?.name || activeProjectId || '';
    const count = activeProject?.document_count;
    label = (
      <>
        שיחה על פרויקט:{' '}
        <Link
          to={`/p/${activeProjectId}/files`}
          className="font-medium underline-offset-2 hover:underline"
          title="הצג קבצים בפרויקט"
        >
          {name}
          {typeof count === 'number' ? ` (${count} מסמכים)` : ''}
        </Link>
      </>
    );
    onClear = onClearProject;
    clearTitle = 'יציאה מהקשר הפרויקט';
    tone = 'project';
  }

  const toneClass = tone === 'doc'
    ? 'bg-indigo-50 text-indigo-700 border-indigo-100'
    : tone === 'project'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
      : 'bg-gray-50 text-gray-600 border-gray-200';

  return (
    <div className="px-4 pt-2" dir="rtl">
      <div className="max-w-3xl mx-auto flex flex-wrap items-center gap-2">
        <div className={`inline-flex items-center gap-2 text-xs border rounded-full px-3 py-1 ${toneClass}`}>
          <span aria-hidden="true">{icon}</span>
          <span className="truncate max-w-[56ch]">{label}</span>
          {processingCount > 0 && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap"
              title="מסמכים שעדיין בעיבוד — התשובות יתבססו עליהם רק כשיהיו מוכנים"
            >
              <Loader2 className="h-2.5 w-2.5 animate-spin" />
              {processingCount === 1 ? 'מסמך בעיבוד' : `${processingCount} בעיבוד`}
            </span>
          )}
          {onClear && (
            <button
              onClick={onClear}
              title={clearTitle}
              className="opacity-60 hover:opacity-100 transition-opacity"
              aria-label={clearTitle}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        {extraAction}
      </div>
    </div>
  );
};

function buildPlaceholder(args: {
  activeRecipeId: string | null;
  selectedFileCount: number;
  activeProject: ChatActiveProject | null;
  activeProjectId: string | null;
}): string {
  if (args.activeRecipeId) return 'שאל שאלה על הדוח או שלח לניתוח...';
  if (args.selectedFileCount === 1) return 'שאל שאלה על המסמך שנבחר...';
  if (args.selectedFileCount > 1) return `שאל שאלה על ${args.selectedFileCount} המסמכים שנבחרו...`;
  if (args.activeProjectId) {
    const count = args.activeProject?.document_count;
    return typeof count === 'number'
      ? `שאל שאלה על ${count} המסמכים בפרויקט...`
      : 'שאל שאלה על הפרויקט...';
  }
  return 'שאל שאלה על הדוחות שלך...';
}

export default ChatArea;
